// src/clips.ts
var PITCH_CLIP_SOURCES = [
  "youtube",
  "vimeo",
  "wistia",
  "loom",
  "mp4",
  "native_bunny"
];
function isPitchClipSource(value) {
  return PITCH_CLIP_SOURCES.includes(value);
}
function normalizeClipRow(row) {
  const id = String(row.id ?? "").trim();
  const sourceRaw = String(row.source ?? "");
  if (!id || !isPitchClipSource(sourceRaw)) return null;
  const duration = row.durationSeconds;
  return {
    id,
    source: sourceRaw,
    externalId: row.externalId != null ? String(row.externalId) : null,
    externalUrl: row.externalUrl != null ? String(row.externalUrl) : null,
    bunnyId: row.bunnyId != null ? String(row.bunnyId) : null,
    durationSeconds: typeof duration === "number" && Number.isFinite(duration) ? duration : null,
    title: row.title != null ? String(row.title) : void 0
  };
}
function parseClipsJson(raw) {
  if (raw == null) return [];
  if (Array.isArray(raw)) {
    return raw.map(
      (c) => c && typeof c === "object" ? normalizeClipRow(c) : null
    ).filter((c) => c != null);
  }
  const trimmed = String(raw).trim();
  if (!trimmed) return [];
  try {
    const parsed = JSON.parse(trimmed);
    if (!Array.isArray(parsed)) return [];
    return parseClipsJson(parsed);
  } catch {
    return [];
  }
}
function synthesizeClipFromLegacyRow(row) {
  const sourceRaw = String(row.videoSource ?? "").trim();
  if (!isPitchClipSource(sourceRaw)) return [];
  const explicitExternalId = row.videoExternalId != null && String(row.videoExternalId).length > 0 ? String(row.videoExternalId) : null;
  const externalUrl = row.videoExternalUrl != null ? String(row.videoExternalUrl) : null;
  const fallbackExternalId = explicitExternalId ?? extractExternalIdFromUrl(sourceRaw, externalUrl);
  return [
    {
      id: "main",
      source: sourceRaw,
      externalId: fallbackExternalId,
      externalUrl,
      bunnyId: row.bunnyVideoId != null ? String(row.bunnyVideoId) : null,
      durationSeconds: null,
      title: void 0
    }
  ];
}
function extractExternalIdFromUrl(declaredSource, url) {
  if (!url) return null;
  const detected = detectPitchClipSourceFromUrl(url);
  if (!detected) return null;
  if (detected.source !== declaredSource) return null;
  return detected.externalId;
}
function normalizeClips(row) {
  const explicit = parseClipsJson(
    row.clips
  );
  if (explicit.length > 0) return explicit;
  return synthesizeClipFromLegacyRow(row);
}
function synthesizeClipFromSection(section, join) {
  const source = section.videoSource;
  if (source === "url") {
    const url = (section.videoExternalUrl ?? "").trim();
    if (!url) return null;
    const detected = detectPitchClipSourceFromUrl(url);
    if (!detected) return null;
    const clip = {
      id: "main",
      source: detected.source,
      externalId: detected.externalId,
      externalUrl: url,
      bunnyId: null,
      durationSeconds: null,
      title: void 0
    };
    const poster = (section.videoPosterFileId ?? "").trim();
    if (poster && join?.posterCdnUrl) {
      clip.posterUrl = join.posterCdnUrl;
    }
    return clip;
  }
  if (source === "bunny") {
    const bunnyId = (section.bunnyVideoId ?? "").trim();
    if (!bunnyId) return null;
    if (!join || !join.hlsUrl) return null;
    const clip = {
      id: "main",
      source: "native_bunny",
      externalId: bunnyId,
      externalUrl: join.hlsUrl,
      bunnyId,
      durationSeconds: typeof join.durationSec === "number" && Number.isFinite(join.durationSec) ? join.durationSec : null,
      title: join.title ? String(join.title) : void 0
    };
    const poster = (section.videoPosterFileId ?? "").trim();
    if (poster && join.posterCdnUrl) {
      clip.posterUrl = join.posterCdnUrl;
    }
    return clip;
  }
  return null;
}
function detectPitchClipSourceFromUrl(url) {
  let parsed;
  try {
    parsed = new URL(url);
  } catch {
    return null;
  }
  const host = parsed.hostname.toLowerCase();
  if (host === "youtu.be") {
    const id = parsed.pathname.split("/").filter(Boolean)[0];
    return id ? { source: "youtube", externalId: id } : null;
  }
  if (host === "www.youtube.com" || host === "youtube.com") {
    const id = parsed.searchParams.get("v");
    return id ? { source: "youtube", externalId: id } : null;
  }
  if (host === "player.vimeo.com" || host === "vimeo.com") {
    const segments = parsed.pathname.split("/").filter(Boolean);
    const id = segments[segments.length - 1];
    return id ? { source: "vimeo", externalId: id } : null;
  }
  return { source: "mp4", externalId: null };
}

// src/layout.ts
var PITCH_LAYOUT_IDS = ["stage"];
var DEFAULT_LAYOUT = "stage";
var LEGACY_IDS = /* @__PURE__ */ new Set(["current", "wide-v2"]);
function isPitchLayoutId(value) {
  return PITCH_LAYOUT_IDS.includes(value);
}
function normalizePitchLayout(row) {
  const raw = String(row.layout ?? "").trim();
  if (isPitchLayoutId(raw)) return raw;
  if (LEGACY_IDS.has(raw)) return DEFAULT_LAYOUT;
  return DEFAULT_LAYOUT;
}

// src/sections.ts
import { marked } from "marked";

// src/render-section-body.ts
import sanitizeHtml from "sanitize-html";
var ALLOWED_TAGS = [
  "p",
  "br",
  "hr",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "ul",
  "ol",
  "li",
  "blockquote",
  "strong",
  "em",
  "code",
  "pre",
  "a",
  "table",
  "thead",
  "tbody",
  "tr",
  "th",
  "td",
  "del",
  // Inline SVG — added 2026-06-12 to let creators embed presentation-only
  // SVGs (charts, icons, diagrams) directly in a section's markdown body.
  // The XSS surface is closed by the `nonTextTags` block (which already
  // includes "script" and "noscript"), the `<foreignObject>` removal in
  // `ALLOWED_SVG_TAGS` (the only SVG element that can host HTML), and the
  // narrow `ALLOWED_SVG_ATTRS` allowlist (no `on*` handlers, no
  // `xlink:href` to non-`http(s)` URLs, no `style` with `expression()`).
  "svg",
  "g",
  "path",
  "rect",
  "circle",
  "ellipse",
  "line",
  "polyline",
  "polygon",
  "text",
  "tspan",
  "defs",
  "linearGradient",
  "radialGradient",
  "stop",
  "title",
  "desc",
  "use",
  "symbol",
  "clipPath",
  "mask",
  "pattern"
];
var ALLOWED_HREF_SCHEMES = ["http", "https", "mailto"];
var ALLOWED_SVG_ATTRS = [
  "viewBox",
  "xmlns",
  "xmlns:xlink",
  "x",
  "y",
  "x1",
  "y1",
  "x2",
  "y2",
  "width",
  "height",
  "cx",
  "cy",
  "r",
  "rx",
  "ry",
  "d",
  "transform",
  "fill",
  "stroke",
  "stroke-width",
  "stroke-linecap",
  "stroke-linejoin",
  "stroke-dasharray",
  "stroke-miterlimit",
  "stroke-opacity",
  "fill-opacity",
  "fill-rule",
  "opacity",
  "color",
  "gradientUnits",
  "gradientTransform",
  "spreadMethod",
  "offset",
  "stop-color",
  "stop-opacity",
  "text-anchor",
  "font-family",
  "font-size",
  "font-weight",
  "font-style",
  "letter-spacing",
  "word-spacing",
  "dx",
  "dy",
  "rotate",
  "textLength",
  "lengthAdjust",
  "class",
  "id",
  "role",
  "aria-label",
  "aria-hidden",
  "aria-labelledby",
  "aria-describedby",
  "preserveAspectRatio",
  "clip-path",
  "mask",
  "filter",
  "points",
  "marker-start",
  "marker-mid",
  "marker-end"
];
function sanitizeSectionHtml(html) {
  const svgElementNames = /* @__PURE__ */ new Set([
    "svg",
    "g",
    "path",
    "rect",
    "circle",
    "ellipse",
    "line",
    "polyline",
    "polygon",
    "text",
    "tspan",
    "defs",
    "linearGradient",
    "radialGradient",
    "stop",
    "title",
    "desc",
    "use",
    "symbol",
    "clipPath",
    "mask",
    "pattern"
  ]);
  const allowedAttributes = {
    a: ["href", "title", "rel", "target"],
    th: ["align"],
    td: ["align"]
  };
  for (const tag of svgElementNames) {
    allowedAttributes[tag] = [...ALLOWED_SVG_ATTRS];
  }
  return sanitizeHtml(html, {
    allowedTags: [...ALLOWED_TAGS],
    allowedAttributes,
    allowedSchemes: [...ALLOWED_HREF_SCHEMES],
    allowedSchemesByTag: {
      a: [...ALLOWED_HREF_SCHEMES]
    },
    allowedSchemesAppliedToAttributes: ["href", "xlink:href"],
    // Disable htmlparser2's default lowercasing of tag and attribute
    // names. SVG is case-sensitive (`viewBox` != `viewbox`; `linearGradient`
    // != `lineargradient`), and our allowlists intentionally use the
    // canonical mixed-case spellings from the SVG spec. With the
    // defaults on, sanitize-html silently drops `viewBox` even when it
    // is in `allowedAttributes.svg` (this is a known, long-standing
    // sanitize-html behaviour — see apostrophecms/sanitize-html #276
    // and the v2 "SVG support" section of the README). The trade-off
    // is that the allowlist itself is now case-sensitive too; the
    // ALLOWED_TAGS / ALLOWED_SVG_ATTRS constants in this file are the
    // single source of truth and use the canonical spellings. A
    // regression test in render-section-body.test.ts pins that
    // `viewBox` survives the sanitizer round-trip.
    parser: {
      lowerCaseTags: false,
      lowerCaseAttributeNames: false
    },
    // Force safe defaults on every surviving <a>.
    transformTags: {
      a: (tagName, attribs) => {
        const href = typeof attribs.href === "string" ? attribs.href : "";
        const safe = isSafeUrl(href);
        const next = {};
        if (safe) {
          next.href = href;
          if (/^https?:\/\//i.test(href)) {
            next.rel = "nofollow noopener";
            next.target = "_blank";
          }
        }
        if (typeof attribs.title === "string") {
          next.title = attribs.title;
        }
        return { tagName, attribs: next };
      }
    },
    // Strip the entire contents of raw-text elements that have
    // historically been used to bypass sanitizers (e.g. the
    // 2026-05 sanitize-html <xmp> CVE). We do not need any of these.
    // `script` and `noscript` are the SVG XSS vector; the SVG allowlist
    // does not include them so they fall through to this list and get
    // text-stripped.
    nonTextTags: ["script", "style", "textarea", "option", "xmp", "noscript"],
    // When we see a disallowed tag, drop the tag but keep its text.
    // (This is the documented default; set explicitly so a future
    // sanitize-html default change does not silently flip the
    // behaviour.)
    disallowedTagsMode: "discard",
    // Always self-close void elements so output is stable for
    // snapshot tests and Bunny CDN caching.
    selfClosing: ["br", "hr"]
  });
}
function isSafeUrl(url) {
  if (!url) return false;
  if (/[\u0000-\u001F\u007F\s]/.test(url)) return false;
  const lower = url.toLowerCase();
  for (const scheme of ALLOWED_HREF_SCHEMES) {
    if (lower.startsWith(`${scheme}:`)) return true;
  }
  return false;
}

// src/sections.ts
var SECTION_KINDS = [
  "problem",
  "agitate",
  "solution",
  "hook",
  "interest",
  "desire",
  "action",
  "before",
  "after",
  "bridge",
  "feature",
  "advantage",
  "benefit",
  "promise",
  "picture",
  "proof",
  "push",
  "freeform",
  "hook_teaser",
  "pain_deep_dive",
  "solution_reveal",
  "demo_mechanics",
  "proof_testimonials",
  "objection_handler",
  "offer_stack",
  "cta_close",
  "roll_in_recap",
  "easy_who_its_for",
  "easy_one_sentence_promise",
  "easy_proof_or_cta"
];
function serializeSectionForRow(pitchId, order, section) {
  return {
    pitchId,
    order,
    kind: section.kind,
    body: section.body,
    mediaIds: [...section.mediaIds],
    cues: section.cues && section.cues.length > 0 ? JSON.stringify(section.cues) : null,
    headline: section.headline ?? null,
    sub: section.sub ?? null,
    videoSource: section.videoSource ?? null,
    videoExternalUrl: section.videoExternalUrl ?? null,
    bunnyVideoId: section.bunnyVideoId ?? null,
    videoPosterFileId: section.videoPosterFileId ?? null
  };
}
function deserializeRowToSection(row) {
  if (!isSectionKind(row.kind)) return null;
  let cues;
  if (typeof row.cues === "string" && row.cues.trim()) {
    try {
      const parsed = JSON.parse(row.cues);
      if (Array.isArray(parsed)) {
        cues = parsed.filter(
          (c) => !!c && typeof c === "object" && typeof c.clipId === "string" && typeof c.startSeconds === "number"
        ).map((c) => ({
          clipId: c.clipId,
          startSeconds: c.startSeconds,
          ...c.endSeconds != null ? { endSeconds: c.endSeconds } : {}
        }));
      }
    } catch {
    }
  }
  const section = {
    kind: row.kind,
    body: row.body,
    mediaIds: Array.isArray(row.mediaIds) ? [...row.mediaIds] : []
  };
  if (cues && cues.length > 0) section.cues = cues;
  if (row.headline && row.headline.trim()) section.headline = row.headline.trim();
  if (row.sub && row.sub.trim()) section.sub = row.sub.trim();
  if (row.videoSource === "url" && row.videoExternalUrl) {
    section.videoSource = "url";
    section.videoExternalUrl = row.videoExternalUrl;
  } else if (row.videoSource === "bunny" && row.bunnyVideoId) {
    section.videoSource = "bunny";
    section.bunnyVideoId = row.bunnyVideoId;
  }
  if (row.videoPosterFileId && row.videoPosterFileId.trim()) {
    section.videoPosterFileId = row.videoPosterFileId.trim();
  }
  return section;
}
function isSectionKind(value) {
  return SECTION_KINDS.includes(value);
}
function sectionKindTitle(kind) {
  const titles = {
    problem: "Problem",
    agitate: "Agitate",
    solution: "Solution",
    hook: "Attention",
    interest: "Interest",
    desire: "Desire",
    action: "Action",
    before: "Before",
    after: "After",
    bridge: "Bridge",
    feature: "Features",
    advantage: "Advantages",
    benefit: "Benefits",
    promise: "Promise",
    picture: "Picture",
    proof: "Proof",
    push: "Push",
    freeform: "Note",
    hook_teaser: "Hook / Teaser",
    pain_deep_dive: "Pain Deep Dive",
    solution_reveal: "Solution Reveal",
    demo_mechanics: "Demo / Mechanics",
    proof_testimonials: "Proof / Testimonials",
    objection_handler: "Objection Handler",
    offer_stack: "Offer / Stack",
    cta_close: "CTA / Close",
    roll_in_recap: "Roll-in Recap",
    // Easy First Pitch kinds (PFP-1 §3.3) — the "minimum viable"
    // surface for first-time indie builders. The titles are the
    // operator-approved display names from
    // `docs/templates.md` §"Easy First Pitch (PFP-1) — 3 segments,
    // 15-minute target".
    easy_who_its_for: "Who It's For",
    easy_one_sentence_promise: "The One-Sentence Promise",
    easy_proof_or_cta: "Proof or CTA"
  };
  return titles[kind] ?? kind;
}
function normalizeSectionCues(raw) {
  if (!Array.isArray(raw) || raw.length === 0) return void 0;
  const out = [];
  for (const row of raw) {
    if (!row || typeof row !== "object") continue;
    const r = row;
    const clipId = String(r.clipId ?? "").trim();
    const start = Number(r.startSeconds);
    if (!clipId || !Number.isFinite(start)) continue;
    const endRaw = r.endSeconds;
    const cue = { clipId, startSeconds: start };
    if (endRaw != null && Number.isFinite(Number(endRaw))) {
      cue.endSeconds = Number(endRaw);
    }
    out.push(cue);
  }
  return out.length > 0 ? out : void 0;
}
function parseSectionsJson(raw) {
  if (raw == null) return [];
  if (Array.isArray(raw)) return normalizeSections(raw);
  const trimmed = String(raw).trim();
  if (!trimmed) return [];
  try {
    const parsed = JSON.parse(trimmed);
    if (!Array.isArray(parsed)) return [];
    return normalizeSections(parsed);
  } catch {
    return [];
  }
}
function normalizeSections(rows) {
  const out = [];
  for (const row of rows) {
    if (!row || typeof row !== "object") continue;
    const r = row;
    const kind = String(r.kind ?? "");
    if (!isSectionKind(kind)) continue;
    const cues = normalizeSectionCues(r.cues);
    const headlineRaw = r.headline;
    const subRaw = r.sub;
    const headline = typeof headlineRaw === "string" && headlineRaw.trim() ? headlineRaw.trim() : void 0;
    const sub = typeof subRaw === "string" && subRaw.trim() ? subRaw.trim() : void 0;
    const section = {
      kind,
      body: String(r.body ?? ""),
      mediaIds: Array.isArray(r.mediaIds) ? r.mediaIds.map((id) => String(id)) : []
    };
    if (cues) section.cues = cues;
    if (headline) section.headline = headline;
    if (sub) section.sub = sub;
    const videoSourceRaw = r.videoSource;
    if (videoSourceRaw === "url" || videoSourceRaw === "bunny") {
      section.videoSource = videoSourceRaw;
      if (videoSourceRaw === "url") {
        const urlRaw = r.videoExternalUrl;
        if (typeof urlRaw === "string" && urlRaw.length > 0) {
          section.videoExternalUrl = urlRaw;
        }
      } else {
        const idRaw = r.bunnyVideoId;
        if (typeof idRaw === "string" && idRaw.length > 0) {
          section.bunnyVideoId = idRaw;
        }
      }
    }
    const posterRaw = r.videoPosterFileId;
    if (typeof posterRaw === "string" && posterRaw.length > 0) {
      section.videoPosterFileId = posterRaw;
    }
    out.push(section);
  }
  return out;
}
function parsePitchSectionsColumn(raw) {
  return parseSectionsJson(raw);
}
function pitchSectionsRowsToArray(rows, query) {
  const filtered = query ? rows.filter((r) => r.pitchId === query.pitchId) : rows;
  const sorted = [...filtered].sort((a, b) => a.order - b.order);
  const out = [];
  for (const row of sorted) {
    const section = deserializeRowToSection(row);
    if (section) out.push(section);
  }
  return out;
}
function validateSectionsForFramework(framework, sections) {
  if (framework === "custom") {
    if (sections.length === 0) return { ok: false, reason: "empty" };
    return { ok: true };
  }
  const required = FRAMEWORK_SECTIONS[framework];
  if (sections.length === 0) {
    return { ok: false, reason: "empty", missing: [...required] };
  }
  const present = new Set(sections.map((s) => s.kind));
  const missing = required.filter((k) => !present.has(k));
  if (missing.length > 0) {
    return { ok: false, reason: "missing_kind", missing };
  }
  for (const s of sections) {
    if (!isSectionKind(s.kind)) {
      return { ok: false, reason: "invalid_kind" };
    }
  }
  return { ok: true };
}
function sectionsFromDescription(description) {
  return [{ kind: "problem", body: description, mediaIds: [] }];
}
function normalizePitchContent(row) {
  const frameworkRaw = String(row.framework ?? "pas");
  const framework = isFrameworkId(frameworkRaw) ? frameworkRaw : "pas";
  const parsed = parseSectionsJson(
    row.sections
  );
  const clips = normalizeClips(row);
  const layout = normalizePitchLayout(row);
  if (parsed.length > 0) {
    return { framework, sections: parsed, clips, layout };
  }
  const description = String(row.description ?? "");
  return {
    framework,
    sections: sectionsFromDescription(description),
    clips,
    layout
  };
}
function renderSectionBody(body) {
  if (body == null) return "";
  const text = String(body).replace(/\r\n?/g, "\n");
  if (!text.trim()) return "";
  const raw = marked.parse(text, {
    gfm: true,
    breaks: true,
    async: false
  });
  return sanitizeSectionHtml(raw);
}

// src/frameworks.ts
import JSON5 from "json5";

// src/frameworks/drtv-classic-infomercial.json5
var drtv_classic_infomercial_default = '// Framework: Classic Infomercial (DRTV 28:30 spot)\n// Internet-age default: target ~90 seconds. Scale to 28:30 only when\n// the content is genuinely compelling. The 9-segment spine below is\n// the canonical infomercial order; reorder only with intent.\n{\n  id: "drtv_classic_infomercial",\n  name: "Classic Infomercial (DRTV 28:30 spot)",\n  description: "The canonical direct-response TV infomercial structure. 9 segments: hook the viewer, hold the attention, prove it works, close. Default internet target ~90s; the classical 28:30 ambition is shown as a guideline, not enforced.",\n  origin: "infomercial",\n  // Ordered, gap-free 9-segment spine. Each id is a known SectionKind.\n  segmentSequence: [\n    "hook_teaser",\n    "pain_deep_dive",\n    "solution_reveal",\n    "demo_mechanics",\n    "proof_testimonials",\n    "objection_handler",\n    "offer_stack",\n    "cta_close",\n    "roll_in_recap",\n  ],\n  // Guideline only \u2014 not enforced at validation. The internet-age\n  // default is ~90s; the 28:30 number is the classical TV ambition.\n  suggestedTotalDurationSeconds: 90,\n  defaultCtaKind: "founding_pitcher",\n  notes: "Skim any segment and the pitch dies. The Pain Deep Dive is intentionally long \u2014 that\'s where the soil is built for the Solution Reveal. The Roll-in Recap is the last segment; for sub-90s cuts, drop the roll-in (set framework to `drtv_long_form_spot` only if you need multiple roll-ins).",\n}\n';

// src/frameworks/drtv-long-form-spot.json5
var drtv_long_form_spot_default = `// Framework: Long-Form Spot (DRTV 58:30 program)
// For a true 58:30 (or ambitious 3-5 minute) program-length commercial.
// Doubles up on demo, proof, and roll-ins. Internet-age default ~180s;
// the 58:30 number is the classical TV ambition.
{
  id: "drtv_long_form_spot",
  name: "Long-Form Spot (DRTV 58:30 program)",
  description: "Program-length commercial structure. Doubles up on demo, proof, and roll-ins. Default internet target ~180s; the classical 58:30 ambition is shown as a guideline, not enforced.",
  origin: "infomercial",
  // 14-segment program-length spine: hook + double-deep-dive +
  // double-demo + multi-proof + multi-roll-in. Each id is a known
  // SectionKind.
  segmentSequence: [
    "hook_teaser",            // 0:00-0:08  re-hook
    "pain_deep_dive",         // 0:08-0:30  the cost of doing nothing
    "roll_in_recap",          // 0:30-1:00  re-grab, then the offer teaser
    "solution_reveal",        // 1:00-1:15  the reveal
    "demo_mechanics",         // 1:15-2:00  the first full demo
    "proof_testimonials",     // 2:00-2:25  verified buyers
    "objection_handler",      // 2:25-2:45  the 3 biggest objections
    "demo_mechanics",         // 2:45-3:15  the second demo (different input)
    "proof_testimonials",     // 3:15-3:40  a different proof
    "objection_handler",      // 3:40-4:00  the next objection
    "offer_stack",            // 4:00-4:25  the deal, the bonuses, the scarcity
    "roll_in_recap",          // 4:25-4:45  the second roll-in
    "cta_close",              // 4:45-4:55  the close
    "cta_close",              // 4:55-5:00  the final close + HP code
  ],
  suggestedTotalDurationSeconds: 180,
  defaultCtaKind: "founding_pitcher",
  notes: "For a 58:30 cut, multiply each suggested segment duration by ~16. For an internet-age 3-minute cut, leave the 180s guideline as-is. Multiple roll-ins are intentional \u2014 they're the channel-surfer's re-entry point.",
}
`;

// src/frameworks/easy-first-pitch.json5
var easy_first_pitch_default = '// Framework: Easy First Pitch (PFP-1 \xA73.3)\r\n// 3-section minimum-viable pitch template for first-time indie\r\n// builders. The 3 kinds (`easy_who_its_for` /\r\n// `easy_one_sentence_promise` / `easy_proof_or_cta`) are first-class\r\n// additions to the SectionKind union, NOT aliases of the DRTV\r\n// kinds \u2014 they are the "minimum viable" surface, and the DRTV\r\n// kinds are the "fully-scripted" surface (per `docs/templates.md`).\r\n//\r\n// Why a new framework id rather than reusing `pas` or\r\n// `drtv_classic_infomercial`:\r\n//   1. The plan calls for the Easy First Pitch to appear as the\r\n//      TOP CARD in the framework picker for new users (per the\r\n//      PFP-1 open decision \xA75.1; default: "top card, not default").\r\n//      Reusing an existing id would conflate the "minimum viable"\r\n//      surface with the "fully-scripted" one.\r\n//   2. The `origin: "direct_response"` value distinguishes the\r\n//      framework from the `copywriting` (PAS/AIDA/BAB/FAB/PSS/4Ps)\r\n//      and `infomercial` (DRTV 28:30/58:30) origins in the\r\n//      `FrameworkOrigin` enum.\r\n//   3. The 3 new SectionKind values cannot be added to an existing\r\n//      framework\'s `segmentSequence` without expanding the kind\r\n//      union, which the plan requires.\r\n//\r\n// The internet-age default is 90 seconds (matching the\r\n// `drtv_classic_infomercial` guideline from `templates.md`); the\r\n// "first pitch in 15 minutes" target (PFP-1 \xA77) is the operator\r\n// goal, not the framework\'s duration.\r\n{\r\n  id: "easy_first_pitch",\r\n  name: "Easy First Pitch",\r\n  description: "Three-section minimum-viable pitch for first-time indie builders. Who it\'s for \u2192 the one-sentence promise \u2192 the smallest proof or CTA. \'Crappy but live beats perfect late\' (marketing-and-promotion.md \xA72.8).",\r\n  origin: "direct_response",\r\n  segmentSequence: [\r\n    "easy_who_its_for",\r\n    "easy_one_sentence_promise",\r\n    "easy_proof_or_cta",\r\n  ],\r\n  suggestedTotalDurationSeconds: 90,\r\n  defaultCtaKind: "founding_pitcher",\r\n  notes: "The \'Easy First Pitch\' is the studio\'s on-ramp framework for first-time pitchers (per the PFP-1 sprint plan \xA73.3 and `docs/templates.md` \xA7\'Easy First Pitch (PFP-1) \u2014 3 segments, 15-minute target\'). As of the 2026-06-19 rework (see `.cursor/plans/rework_easy_first_pitch_into_a_top-card_framework_*.plan.md`), the framework is rendered as the TOP CARD of the standard framework picker (`listFrameworksForWizard()` returns it at index 0), with a visible \'Quick start\' badge and a \'Use 3-step quick start\' CTA that mounts the 3-step wizard as a card-level overlay. It is no longer a separate mode of the `/pitches/new` shell. The framework is one of ten (7 copywriting + 2 DRTV + 1 easy), not a replacement for the others. The 3 new SectionKind values are first-class additions to the union, not aliases. The 90-second guideline is the internet-age default; the operator\'s \'first pitch in 15 minutes\' is the time-to-publish target, not the segment duration.",\r\n}\r\n';

// src/frameworks.ts
var rawFrameworkJsons = [
  drtv_classic_infomercial_default,
  drtv_long_form_spot_default,
  // Easy First Pitch (PFP-1 §3.3). The rich definition
  // (description, origin, suggestedTotalDurationSeconds, notes) is
  // loaded from this JSON5. The 3 SectionKind values in its
  // `segmentSequence` are also listed in the canonical
  // `FRAMEWORK_SECTIONS.easy_first_pitch` in `framework.ts` (the
  // SectionKind union must be closed at type-check, so it cannot
  // be lazy-loaded from JSON5). The two lists MUST match; the
  // `every segmentSequence reference resolves to a known
  // SectionKind` test in `frameworks.test.ts` pins the kinds
  // referenced here.
  easy_first_pitch_default
];
var cached = null;
function loadFrameworks() {
  if (cached) return cached;
  const rich = {};
  for (const raw of rawFrameworkJsons) {
    const parsed = JSON5.parse(raw);
    if (!parsed.id) {
      throw new Error(
        `Framework JSON5 file is missing required field 'id':
${raw.slice(0, 200)}`
      );
    }
    if (!Array.isArray(parsed.segmentSequence)) {
      throw new Error(
        `Framework JSON5 file '${parsed.id}' is missing 'segmentSequence'.`
      );
    }
    for (const k of parsed.segmentSequence) {
      if (!isSectionKind(k)) {
        throw new Error(
          `Framework '${parsed.id}' references unknown SectionKind '${k}'.`
        );
      }
    }
    rich[parsed.id] = parsed;
  }
  const out = {};
  for (const id of FRAMEWORK_IDS) {
    if (rich[id]) {
      out[id] = rich[id];
    } else {
      out[id] = synthesizeFromCode(id);
    }
  }
  cached = out;
  return cached;
}
function synthesizeFromCode(id) {
  const seq = FRAMEWORK_SECTIONS[id];
  if (id === "custom") {
    return {
      id,
      name: "Custom \u2014 your structure",
      description: "Free-form. No prescribed order; creators supply freeform sections.",
      origin: "custom",
      segmentSequence: seq
    };
  }
  return {
    id,
    name: id.toUpperCase(),
    description: `In-code copywriting framework.`,
    origin: "copywriting",
    segmentSequence: seq
  };
}
function frameworkDefinition(id) {
  return loadFrameworks()[id];
}
var FRAMEWORK_DEFINITIONS = new Proxy({}, {
  get(_target, prop) {
    if (typeof prop === "string") {
      return loadFrameworks()[prop];
    }
    return void 0;
  },
  ownKeys() {
    return Object.keys(loadFrameworks());
  },
  getOwnPropertyDescriptor() {
    return { enumerable: true, configurable: true };
  }
});

// src/framework.ts
var FRAMEWORK_IDS = [
  // Easy First Pitch (PFP-1 §3.3) — the 10th framework, added in
  // 2026-06. Listed FIRST per the plan's open decision §5.1
  // default: "Top card. Default stays the framework picker
  // (consistent with existing UX)." The 3-section minimum-viable
  // surface for first-time indie builders. New ID; not an alias
  // of any existing framework. See `docs/templates.md` and
  // `.cursor/plans/path-to-first-pitch/sprint-1-easy-first-pitch-template.md`
  // §3.3 for the canonical spec.
  "easy_first_pitch",
  // 2026-06-28: ordered by operator-decided likelihood-of-use. PAS is
  // the default for B2C indie; AIDA / BAB / FAB round out the three
  // classic copywriting frameworks most indie builders reach for;
  // PSS / 4Ps are the more specialised picks (great for technical
  // audiences, less common for the median Pitcher). The order is
  // matched by `framework-wizard.ts → listFrameworksForWizard()` so
  // the picker's left rail and the data model stay in lockstep.
  "pas",
  "aida",
  "bab",
  "fab",
  "pss",
  "4ps",
  // 2026-06-28: DRTV frameworks sit between the copywriting 6 and
  // `custom` so a direct-response TV creator sees their picks before
  // falling through to the escape-hatch `custom` framework.
  "drtv_classic_infomercial",
  "drtv_long_form_spot",
  // 2026-06-28: `custom` is intentionally the LAST option. It is the
  // "you already know what you want" escape hatch — not a first
  // recommendation. Placing it last also matches the studio's
  // MR-natural ordering (default framework options first, escape
  // hatch last) and the operator's "always the last one listed"
  // directive.
  "custom"
];
var FRAMEWORK_LABELS = {
  pas: "PAS \u2014 Problem, Agitate, Solution",
  aida: "AIDA \u2014 Attention, Interest, Desire, Action",
  bab: "BAB \u2014 Before, After, Bridge",
  fab: "FAB \u2014 Features, Advantages, Benefits",
  pss: "PSS \u2014 Problem, Solution, Success",
  "4ps": "4Ps \u2014 Promise, Picture, Proof, Push",
  custom: "Custom \u2014 your structure",
  drtv_classic_infomercial: "DRTV \u2014 Classic Infomercial (28:30 spot)",
  drtv_long_form_spot: "DRTV \u2014 Long-Form Spot (58:30 program)",
  // The Easy First Pitch label deliberately does NOT include a
  // long-form subtitle; the picker uses the card body (the
  // framework JSON5's `description` field) for the operator
  // framing copy. See `docs/templates.md` for the approved copy.
  easy_first_pitch: "Easy First Pitch"
};
var FRAMEWORK_SECTIONS = {
  pas: ["problem", "agitate", "solution"],
  aida: ["hook", "interest", "desire", "action"],
  bab: ["before", "after", "bridge"],
  fab: ["feature", "advantage", "benefit"],
  pss: ["problem", "solution", "proof"],
  "4ps": ["promise", "picture", "proof", "push"],
  custom: ["freeform"],
  drtv_classic_infomercial: [],
  drtv_long_form_spot: [],
  easy_first_pitch: [
    "easy_who_its_for",
    "easy_one_sentence_promise",
    "easy_proof_or_cta"
  ]
};
function isFrameworkId(value) {
  return FRAMEWORK_IDS.includes(value);
}
function frameworkOutlineLabel(framework) {
  if (framework === "custom") return FRAMEWORK_LABELS.custom;
  const short = framework.toUpperCase();
  if (FRAMEWORK_SECTIONS[framework].length === 0) {
    const defs = loadFrameworks();
    const def = defs[framework];
    if (def && def.segmentSequence.length > 0) {
      FRAMEWORK_SECTIONS[framework] = def.segmentSequence;
    }
  }
  const parts = FRAMEWORK_SECTIONS[framework].map((k) => sectionKindTitle(k));
  return `${short}: ${parts.join(" \u2192 ")}`;
}

// src/cues.ts
function sortCues(cues) {
  return [...cues].sort((a, b) => a.startSeconds - b.startSeconds);
}
function validateCues(sections) {
  for (const section of sections) {
    const cues = section.cues;
    if (!cues?.length) continue;
    const sorted = sortCues(cues);
    for (const cue of sorted) {
      if (!cue.clipId.trim()) {
        return { ok: false, reason: "invalid_clip_id", sectionKind: section.kind };
      }
      if (cue.startSeconds < 0) {
        return { ok: false, reason: "invalid_start", sectionKind: section.kind };
      }
      if (cue.endSeconds != null && cue.endSeconds <= cue.startSeconds) {
        return { ok: false, reason: "invalid_range", sectionKind: section.kind };
      }
    }
    for (let i = 1; i < sorted.length; i++) {
      const prev = sorted[i - 1];
      const curr = sorted[i];
      const prevEnd = prev.endSeconds ?? prev.startSeconds;
      if (curr.startSeconds < prevEnd) {
        return { ok: false, reason: "overlap", sectionKind: section.kind };
      }
    }
  }
  return { ok: true };
}

// src/variants.ts
import { validateCode } from "@honest-pitches/hp-codes";
function baseKey(pitch) {
  const result = validateCode(pitch.hpCode);
  if (!result.valid || !result.payload || !result.checkDigit) {
    return null;
  }
  return result.payload + result.checkDigit;
}
function siblingsByBase(pitch, allPitches, options) {
  const base = baseKey(pitch);
  if (!base) return [];
  const excludeSelf = options?.excludeSelf ?? true;
  return allPitches.filter((candidate) => {
    const candidateBase = baseKey(candidate);
    if (!candidateBase || candidateBase !== base) {
      return false;
    }
    if (excludeSelf && candidate.hpCode === pitch.hpCode) {
      return false;
    }
    return true;
  });
}
function isAudienceVariantPair(a, b) {
  if (!a.intendedAudience || !b.intendedAudience) {
    return false;
  }
  return a.intendedAudience !== b.intendedAudience;
}
function groupByOffer(pitches) {
  const grouped = /* @__PURE__ */ new Map();
  for (const pitch of pitches) {
    if (!pitch.offerId) continue;
    const bucket = grouped.get(pitch.offerId);
    if (bucket) {
      bucket.push(pitch);
    } else {
      grouped.set(pitch.offerId, [pitch]);
    }
  }
  return grouped;
}
function siblingPicker(pitch, allPitches) {
  const base = baseKey(pitch);
  if (!base) return null;
  const siblings = siblingsByBase(pitch, allPitches, { excludeSelf: false });
  return {
    offerId: pitch.offerId,
    base,
    siblings,
    audienceOptions: siblings.map((sibling) => ({
      pitchId: sibling.$id,
      intendedAudience: sibling.intendedAudience
    }))
  };
}
function buildPitchVariantIndexes(pitches) {
  const pitchesByBase = /* @__PURE__ */ new Map();
  for (const pitch of pitches) {
    const base = baseKey(pitch);
    if (!base) continue;
    const bucket = pitchesByBase.get(base);
    if (bucket) {
      bucket.push(pitch);
    } else {
      pitchesByBase.set(base, [pitch]);
    }
  }
  const offerGroups = groupByOffer(pitches);
  return {
    pitchesByBase: Object.fromEntries(pitchesByBase),
    offerGroups: Object.fromEntries(offerGroups)
  };
}

// src/test-types.ts
var TEST_STATUSES = [
  "draft",
  "running",
  "concluded",
  "abandoned"
];
var TEST_ARMS = ["control", "variant_a", "variant_b"];
var TEST_PRIMARY_METRICS = [
  "honest_score",
  "rating_completion",
  "cta_click",
  "purchase"
];
var TEST_CONCLUSIONS = ["winner", "no_winner", "inconclusive"];
var DEFAULT_MIN_SAMPLE_PER_ARM = 25;
function isTestStatus(value) {
  return TEST_STATUSES.includes(value);
}
function isTestArm(value) {
  return TEST_ARMS.includes(value);
}
function isTestPrimaryMetric(value) {
  return TEST_PRIMARY_METRICS.includes(value);
}
function isTestConclusion(value) {
  return TEST_CONCLUSIONS.includes(value);
}

// src/test.ts
var VALID_TEST_TRANSITIONS = {
  draft: ["running", "abandoned"],
  running: ["concluded", "abandoned"],
  concluded: [],
  abandoned: []
};
function canTransitionTest(from, to) {
  if (from === to) {
    return { ok: false, reason: "invalid_transition" };
  }
  const terminal = from === "concluded" || from === "abandoned";
  if (terminal) {
    return { ok: false, reason: "terminal_status" };
  }
  const allowed = VALID_TEST_TRANSITIONS[from];
  if (!allowed.includes(to)) {
    return { ok: false, reason: "invalid_transition" };
  }
  return { ok: true };
}
function canConcludeTest(input) {
  if (input.status !== "running") {
    return { ok: false, reason: "not_running" };
  }
  const counts = Object.values(input.ratingCountsByArm);
  if (counts.length === 0) {
    return { ok: false, reason: "insufficient_sample" };
  }
  if (counts.some((count) => count < input.minSamplePerArm)) {
    return { ok: false, reason: "insufficient_sample" };
  }
  return { ok: true };
}
function assertSameTestBase(pitches) {
  if (pitches.length < 2) {
    return { ok: false, reason: "insufficient_arms" };
  }
  let expectedBase = null;
  let expectedOfferId = null;
  for (const pitch of pitches) {
    const key = baseKey(pitch);
    if (!key) {
      return { ok: false, reason: "invalid_hp_code" };
    }
    if (expectedBase === null) {
      expectedBase = key;
      expectedOfferId = pitch.offerId ?? null;
      continue;
    }
    if (key !== expectedBase) {
      return { ok: false, reason: "base_key_mismatch" };
    }
    const offerId = pitch.offerId ?? null;
    if (expectedOfferId !== offerId) {
      return { ok: false, reason: "offer_id_mismatch" };
    }
  }
  return { ok: true };
}
function validateTestAssignmentCoupling(input) {
  const testId = typeof input.testId === "string" && input.testId.trim() ? input.testId.trim() : null;
  const testArmRaw = typeof input.testArm === "string" && input.testArm.trim() ? input.testArm.trim() : null;
  if (testId && !testArmRaw) {
    return { ok: false, reason: "test_arm_required" };
  }
  if (!testId && testArmRaw) {
    return { ok: false, reason: "test_id_required" };
  }
  if (testArmRaw && !isTestArm(testArmRaw)) {
    return { ok: false, reason: "invalid_test_arm" };
  }
  return { ok: true };
}
function canMutateTestAssignment(pitch, changes, options = {}) {
  if (pitch.openAt && pitch.closeAt) {
    const open = Date.parse(pitch.openAt);
    const close = Date.parse(pitch.closeAt);
    if (!Number.isNaN(open) && !Number.isNaN(close)) {
      const now = options.now ?? pitch.now ?? Date.now();
      if (open <= now && now <= close) {
        if ("testId" in changes || "testArm" in changes) {
          return { ok: false, reason: "active_listing_locked" };
        }
      }
    }
  }
  return { ok: true };
}
function testIdForPitch(pitch) {
  const id = pitch.testId;
  return typeof id === "string" && id.trim() ? id.trim() : null;
}

// src/lifecycle.ts
var PITCH_LIFECYCLE_STATES = [
  "draft",
  "preview",
  "live",
  "expired",
  "archived"
];
function assertFrameworkSwitchable(input) {
  if (input.status === "draft") return { ok: true };
  if (input.status === "live" || input.status === "expired" || input.status === "archived") {
    return { ok: false, reason: "immutable" };
  }
  return { ok: false, reason: "locked" };
}
function canChangeFramework(from, to) {
  if (from === "draft" && to === "draft") return { ok: true };
  if (from === "draft" && to !== "draft") {
    return { ok: false, reason: "locked" };
  }
  return assertFrameworkSwitchable({ status: from });
}
function isPitchLifecycleState(value) {
  return PITCH_LIFECYCLE_STATES.includes(value);
}
function isPaidRatingWindowOpen(pitch, options = {}) {
  const openAtRaw = pitch.openAt;
  const closeAtRaw = pitch.closeAt;
  if (typeof openAtRaw !== "string" || typeof closeAtRaw !== "string") return false;
  if (!openAtRaw.trim() || !closeAtRaw.trim()) return false;
  const open = Date.parse(openAtRaw);
  const close = Date.parse(closeAtRaw);
  if (Number.isNaN(open) || Number.isNaN(close)) return false;
  const now = options.now ?? pitch.now ?? Date.now();
  return open <= now && now <= close;
}
function isEditablePitch(pitch, options = {}) {
  return !isPaidRatingWindowOpen(pitch, options);
}
function lifecycleFromRow(row) {
  const status = String(row.status ?? "draft");
  if (status === "draft" && row.previewToken) return "preview";
  if (isPitchLifecycleState(status)) return status;
  if (status === "pending_payment") return "draft";
  if (status === "removed") return "archived";
  return "draft";
}
function canHardDeleteFromLifecycle(state) {
  return state === "draft";
}
function isHardDeletableFromRow(row) {
  return row.visibility === "archived";
}
function canHardDeleteFromRow(row) {
  const status = String(row.status ?? "");
  if (status === "draft") return true;
  if (status === "pending_payment") return true;
  return false;
}

// src/normalize.ts
function trimString(value) {
  return typeof value === "string" ? value.trim() : "";
}
function isHttpsUrl(value) {
  try {
    const parsed = new URL(value);
    return parsed.protocol === "https:";
  } catch {
    return false;
  }
}
var SLUG_PATTERN = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/;
function validateSlug(slug) {
  if (!SLUG_PATTERN.test(slug) || slug.length > 63) {
    return `slug must match /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/ (1-63 chars, lowercase a-z + 0-9 + hyphen, no leading/trailing hyphen)`;
  }
  return null;
}
function normalizeSections2(raw) {
  if (!Array.isArray(raw)) return null;
  const out = [];
  for (const row of raw) {
    if (!row || typeof row !== "object") continue;
    const kind = row.kind;
    const body = row.body;
    if (typeof kind !== "string" || typeof body !== "string") continue;
    const mediaIds = row.mediaIds;
    const headlineRaw = row.headline;
    const subRaw = row.sub;
    const headline = typeof headlineRaw === "string" && headlineRaw.trim() ? headlineRaw.trim() : void 0;
    const sub = typeof subRaw === "string" && subRaw.trim() ? subRaw.trim() : void 0;
    const section = {
      kind,
      body,
      mediaIds: Array.isArray(mediaIds) ? mediaIds.filter((id) => typeof id === "string") : []
    };
    if (headline !== void 0) section.headline = headline;
    if (sub !== void 0) section.sub = sub;
    out.push(section);
  }
  return out;
}
function normalizeCreatePitchInput(raw) {
  const creatorId = trimString(raw.creatorId);
  const slug = trimString(raw.slug);
  const title = trimString(raw.title);
  const ctaUrl = trimString(raw.ctaUrl);
  const frameworkRaw = trimString(raw.framework);
  if (!creatorId) return { ok: false, error: "creatorId is required" };
  if (!slug) return { ok: false, error: "slug is required" };
  const slugError = validateSlug(slug);
  if (slugError) return { ok: false, error: slugError };
  if (!title) return { ok: false, error: "title is required" };
  if (!ctaUrl) return { ok: false, error: "ctaUrl is required" };
  if (!isHttpsUrl(ctaUrl)) {
    return { ok: false, error: "ctaUrl must be a valid HTTPS URL" };
  }
  if (!frameworkRaw) return { ok: false, error: "framework is required" };
  if (!isFrameworkId(frameworkRaw)) {
    return { ok: false, error: `Unknown framework: ${frameworkRaw}` };
  }
  const sections = normalizeSections2(raw.sections);
  if (!sections || sections.length === 0) {
    return { ok: false, error: "sections are required" };
  }
  const sectionCheck = validateSectionsForFramework(frameworkRaw, sections);
  if (!sectionCheck.ok) {
    if (sectionCheck.reason === "missing_kind") {
      return {
        ok: false,
        error: `Missing sections: ${sectionCheck.missing?.join(", ")}`
      };
    }
    return { ok: false, error: `Invalid sections: ${sectionCheck.reason}` };
  }
  const priceCents = typeof raw.priceCents === "number" && Number.isFinite(raw.priceCents) ? Math.max(0, Math.round(raw.priceCents)) : 0;
  const hpCodeRaw = trimString(raw.hpCode);
  const hasTestId = raw.testId !== void 0 && raw.testId !== null;
  const hasTestArm = raw.testArm !== void 0 && raw.testArm !== null;
  if (hasTestId || hasTestArm) {
    const testIdRaw2 = raw.testId === null || raw.testId === void 0 ? null : trimString(raw.testId);
    const testArmRaw2 = raw.testArm === null || raw.testArm === void 0 ? null : trimString(raw.testArm);
    const assignment = validateTestAssignmentCoupling({
      testId: testIdRaw2,
      testArm: testArmRaw2
    });
    if (!assignment.ok) {
      if (assignment.reason === "test_arm_required") {
        return { ok: false, error: "testArm is required when testId is set" };
      }
      if (assignment.reason === "test_id_required") {
        return { ok: false, error: "testId is required when testArm is set" };
      }
      return { ok: false, error: `Invalid testArm: ${testArmRaw2}` };
    }
  }
  const testIdRaw = raw.testId === null || raw.testId === void 0 ? void 0 : trimString(raw.testId) || void 0;
  const testArmRaw = raw.testArm === null || raw.testArm === void 0 ? void 0 : trimString(raw.testArm) || void 0;
  return {
    ok: true,
    input: {
      creatorId,
      slug,
      title,
      framework: frameworkRaw,
      sections,
      ctaUrl,
      ctaLabel: trimString(raw.ctaLabel) || "Get it now",
      intendedAudience: trimString(raw.intendedAudience),
      description: trimString(raw.description),
      priceCents,
      currency: trimString(raw.currency) || "USD",
      visibility: trimString(raw.visibility) || "private",
      // 2026-06-21: `listingMode` removed (clean-break).
      status: trimString(raw.status) || "draft",
      ...hpCodeRaw ? { hpCode: hpCodeRaw } : {},
      ...testIdRaw ? { testId: testIdRaw, testArm: testArmRaw } : {}
    }
  };
}

// src/segments.ts
import JSON52 from "json5";

// src/segments/hook-teaser.json5
var hook_teaser_default = `// Segment definition: Hook / Teaser
// The attention-grabber in the first 30-120 seconds. Dramatic problem
// statement, surprising claim, or demo tease. In the internet age this
// is the entire first 5-10 seconds \u2014 make the scroll stop.
{
  id: "hook_teaser",
  name: "Hook / Teaser",
  description: "Attention-grabber that earns the next 30 seconds of attention. Pose a sharp problem, drop a surprising claim, or tease the demo.",
  promptQuestions: [
    "What's the most shocking, specific way to state the viewer's pain right now?",
    "What's the one result you can credibly promise in 5 seconds?",
    "Is there a visual or stat that stops the scroll?",
    "What's the single most surprising thing about this thing?",
    "Why should the viewer NOT click away in the next 8 seconds?",
  ],
  bodyTemplate: "## Headline\\n[Sharpest version of the pain or the boldest promise \u2014 one line.]\\n\\n## Subhead\\n[Why now, why you, in 8-15 words.]\\n\\n## Open\\n1. Lead with the most visceral version of the pain.\\n2. Tease the demonstration or proof in one sentence.\\n3. Land the hook: 'But stay \u2014 here's what we found.'",
  suggestedDurationSeconds: 8,
  layoutHint: "hook-teaser",
  notes: "If you can't write the hook in under 30 seconds, you don't have a hook \u2014 you have an intro. Cut.",
}
`;

// src/segments/pain-deep-dive.json5
var pain_deep_dive_default = `// Segment definition: Pain Deep Dive
// "Before" state. Lingers in the frustration to build dissatisfaction
// with the status quo. Classic infomercials spend disproportionate time
// here \u2014 the longer the pain, the bigger the relief when you arrive.
{
  id: "pain_deep_dive",
  name: "Pain Deep Dive",
  description: "Deep dive into the viewer's frustration. Builds empathy, names the cost, makes the status quo feel unbearable.",
  promptQuestions: [
    "What does the viewer's Tuesday morning actually look like without this?",
    "What has the viewer already tried that didn't work?",
    "What's the cost of the pain \u2014 money, time, dignity, relationships?",
    "Why is the pain getting worse, not better?",
    "What does the viewer secretly fear the pain means about them?",
  ],
  bodyTemplate: "## The Daily Friction\\n[Walk through the viewer's day. Make it specific and a little uncomfortable to read.]\\n\\n## What You've Already Tried\\n- Attempt 1 \u2014 why it didn't work.\\n- Attempt 2 \u2014 why it didn't work.\\n\\n## The Cost of Doing Nothing\\n[Time, money, status, relationships. Quantify if you can.]",
  suggestedDurationSeconds: 30,
  layoutHint: "pain-deep-dive",
  notes: "Don't rush this beat. The pain is the soil the solution grows in. If you skim, the relief lands soft.",
}
`;

// src/segments/solution-reveal.json5
var solution_reveal_default = `// Segment definition: Solution Reveal
// The "But wait \u2014 there's more!" beat. Present your thing as the
// obvious fix. Often delivered with a dramatic transition out of the
// pain: "What if you didn't have to\u2026"
{
  id: "solution_reveal",
  name: "Solution Reveal",
  description: "The dramatic reveal of your thing as the obvious fix to the pain just described. Sets up the demo and the offer.",
  promptQuestions: [
    "What's the simplest one-sentence description of what your thing does?",
    "What category of solution is it (and what category is it NOT)?",
    "Why is your thing the obvious, not the clever, answer?",
    "What changes the moment the viewer uses it?",
    "What makes it permanent / always-on vs a one-time hack?",
  ],
  bodyTemplate: "## The Reveal\\n[One sentence: what this is. No adjectives. No features.]\\n\\n## Why It's Different\\n[One sentence: what category it lives in AND what it does that category-defining thing it doesn't.]\\n\\n## The Promise\\n[In one line, the new state of the world for the viewer.]",
  suggestedDurationSeconds: 15,
  layoutHint: "solution-reveal",
  notes: "Resist the urge to list features here. The reveal is one clean idea. Features come in the demo.",
}
`;

// src/segments/demo-mechanics.json5
var demo_mechanics_default = `// Segment definition: Demo / Mechanics
// "Show, don't tell." Live or dramatized proof it works. The heart of
// any infomercial. In an Honest Pitches carousel, this is multiple
// slides breaking down how the thing works.
{
  id: "demo_mechanics",
  name: "Demo / Mechanics",
  description: "Show, don't just tell. Live or dramatized proof that the thing works. The heart of the pitch \u2014 this is where belief is built.",
  promptQuestions: [
    "What's the single most convincing 10-second demo moment?",
    "What does the viewer need to see to believe this isn't vapor?",
    "What's the input \u2192 the mechanism \u2192 the output?",
    "What edge case or skeptical question does the demo pre-answer?",
    "Can the viewer reproduce the result themselves in 60 seconds?",
  ],
  bodyTemplate: "## The Demo\\n1. Set up the scenario in one line.\\n2. Run the thing live (or dramatized).\\n3. Land the result \u2014 read out the numbers, the before/after, the reaction.\\n\\n## The Mechanism\\n[The 3-step 'how' \u2014 why does this work when similar things don't?]\\n\\n## Repeat / Scale\\n[Show it working a second time, on a different input, to defeat the 'lucky once' objection.]",
  suggestedDurationSeconds: 45,
  layoutHint: "demo-mechanics",
  notes: "The longest segment is intentional. Skim and the pitch dies. Concrete numbers, real screens, real artifacts \u2014 no mock-ups.",
}
`;

// src/segments/proof-testimonials.json5
var proof_testimonials_default = `// Segment definition: Proof / Testimonials
// Real users raving. Builds credibility. HonestPitches already has
// scoring for this \u2014 use it: pull HonestScores, founder pitches, and
// verified-buyer quotes into a single proof block.
{
  id: "proof_testimonials",
  name: "Proof / Testimonials",
  description: "Social proof module. Real users, real reactions, real receipts. HonestScore, founder pitches, verified-buyer quotes.",
  promptQuestions: [
    "Who is the most credible person who has used this?",
    "What's the most specific, most embarrassing-to-fake, most useful result they got?",
    "What does the HonestScore say about this pitch?",
    "What does the audience say (wall, votes, comments)?",
    "What's the gap between the boldest claim and the most modest verified result?",
  ],
  bodyTemplate: "## The HonestScore\\n[Real number, real time, real votes. No editorial.]\\n\\n## Verified Buyers\\n\u2014 \\"Quote 1\\" \u2014 verified by [method]\\n\u2014 \\"Quote 2\\" \u2014 verified by [method]\\n\\n## Founders / Builders\\n[Funder quotes, mentor notes, press \u2014 keep it short.]",
  suggestedDurationSeconds: 25,
  layoutHint: "proof-testimonials",
  notes: "HonestPitches' whole reason for being. The 'honest' in Honest Pitches is the test, not the slogan. Show the receipts.",
}
`;

// src/segments/objection-handler.json5
var objection_handler_default = `// Segment definition: Objection Handler
// Risk reversal. Address skepticism head-on: "But will it really work
// for me?" Guarantees, comparisons, "why this beats the alternatives."
{
  id: "objection_handler",
  name: "Objection Handler",
  description: "Address the 3-5 biggest objections in the viewer's head. Risk reversal, guarantees, comparisons to alternatives.",
  promptQuestions: [
    "What's the most common 'yeah but\u2026' the viewer is mumbling?",
    "What does the viewer secretly fear this is (scam, vapor, niche)?",
    "How do you reverse the risk? (guarantee, refund, free tier, no-questions)",
    "How does this compare to the next best alternative, honestly?",
    "What would a smart skeptic's smart skeptic say?",
  ],
  bodyTemplate: "## \\"But Will It Work for Me?\\"\\n[Answer in one sentence. The viewer's life is not the average \u2014 show you know that.]\\n\\n## Risk Reversal\\n[Refund window, money-back, no-questions, free trial, Founding Pitcher credits \u2014 concrete and dated.]\\n\\n## Compared To\\n| | This | Alternative |\\n|---|---|---|\\n| | | |",
  suggestedDurationSeconds: 20,
  layoutHint: "objection-handler",
  notes: "Don't enumerate every objection \u2014 pick the 3 that actually kill the sale. Compare honestly or skip the comparison entirely.",
}
`;

// src/segments/offer-stack.json5
var offer_stack_default = `// Segment definition: Offer / Stack
// The deal. Price, extras, scarcity. "Call now and get\u2026" The classic
// infomercial stack \u2014 keep the core offer + 1-3 bonuses, not 47.
{
  id: "offer_stack",
  name: "Offer / Stack",
  description: "The deal. Price, what's included, bonuses, scarcity. Keep the stack honest \u2014 core + 1-3 bonuses, not 47.",
  promptQuestions: [
    "What's the core offer, in one line, at one price?",
    "What 1-3 bonuses or stack items add real value (not filler)?",
    "What's the scarcity mechanic (founding only, first 100, this week)?",
    "What's the anchor price (what it was, what it would cost elsewhere)?",
    "What's the ROI math if the viewer does the multiplication?",
  ],
  bodyTemplate: "## The Core Offer\\n[One line, one price.]\\n\\n## What's Included\\n1. [Core]\\n2. [Bonus 1 \u2014 what it is, why it matters]\\n3. [Bonus 2 \u2014 what it is, why it matters]\\n\\n## Scarcity\\n[Be honest. Founding only, this week, first 100. No fake timers.]",
  suggestedDurationSeconds: 25,
  layoutHint: "offer-stack",
  notes: "The stack is a clarity test. If you can't list the offer + 1-3 bonuses in 60 seconds, your offer is too complicated. Simplify.",
}
`;

// src/segments/cta-close.json5
var cta_close_default = `// Segment definition: CTA / Close
// Repeated, urgent instructions. Toll-free numbers in the TV version.
// On Honest Pitches, the CTA points to the HP code channel and the
// "Join as Founding Pitcher" surface where applicable.
{
  id: "cta_close",
  name: "CTA / Close",
  description: "Clear, repeated, urgent call to action. On Honest Pitches, the CTA points to the HP code channel and the founding/offer surface.",
  promptQuestions: [
    "What's the one action you want the viewer to take in the next 60 seconds?",
    "What's the URL, HP code, or 'Join as Founding Pitcher' link?",
    "What's the friction between the viewer and the click?",
    "Is there an in-product moment (HP code, score, wall) that closes the loop?",
    "What's the 'if not now, never' hook \u2014 used honestly?",
  ],
  bodyTemplate: "## The One Thing\\n[Action, in one line. Verb first.]\\n\\n## Where To Go\\n- [URL]\\n- [HP code]\\n- [Founding Pitcher link, if applicable]\\n\\n## Why Now\\n[One sentence. No fake urgency.]",
  suggestedDurationSeconds: 10,
  layoutHint: "cta-close",
  notes: "Don't bury the verb. The CTA is the whole point of the rest of the pitch \u2014 earn it, then say it loud and clear.",
}
`;

// src/segments/roll-in-recap.json5
var roll_in_recap_default = '// Segment definition: Roll-in / Recap\n// Short internal commercial to re-hook wanderers. In a long-form spot\n// the roll-in is a 60-90 second "but wait, before we go on\u2026" recap\n// of the strongest beats \u2014 demo + proof + offer + CTA, fast.\n{\n  id: "roll_in_recap",\n  name: "Roll-in / Recap",\n  description: "Short internal commercial within a long-form spot. Re-hooks wanderers by compressing demo + proof + offer + CTA into 60-90 seconds.",\n  promptQuestions: [\n    "What are the 3 strongest beats (demo, proof, offer)?",\n    "What\'s the shortest version of each that still lands?",\n    "What\'s the new opening line that re-grabs a channel-surfer?",\n    "Does the roll-in advance the story or just repeat it?",\n    "Should this roll-in end with the CTA, or with a new tease?",\n  ],\n  bodyTemplate: "## Re-hook\\n[One line that re-grabs the viewer who just sat back down.]\\n\\n## The Greatest Hits\\n1. [Tightest demo moment \u2014 10s]\\n2. [Tightest proof \u2014 10s]\\n3. [Tightest offer + CTA \u2014 20s]",\n  suggestedDurationSeconds: 60,\n  layoutHint: "roll-in-recap",\n  notes: "Used only in long-form spots (`drtv_long_form_spot`). Optional for `drtv_classic_infomercial` \u2014 drop in the last 90s.",\n}\n';

// src/segments/easy-who-its-for.json5
var easy_who_its_for_default = `// Segment definition: Easy First Pitch \u2014 Who It's For\r
// First beat of the 3-segment "Easy First Pitch" minimum-viable\r
// framework. Authored for first-time indie builders; intentionally\r
// short and non-judgmental. Maps to the first 1-2 sentences of the\r
// DRTV \`pain_deep_dive\` segment in spirit, but is its own kind\r
// because the Easy First Pitch kinds are the "minimum viable" surface\r
// and the DRTV kinds are the "fully-scripted" surface (per\r
// \`docs/templates.md\` \xA7"Easy First Pitch (PFP-1) \u2014 3 segments,\r
// 15-minute target").\r
{\r
  id: "easy_who_its_for",\r
  name: "Who It's For",\r
  description: "Name the one person this pitch is for. One short paragraph; the audience is the first beat, not the whole problem space.",\r
  promptQuestions: [\r
    "Who is the one person who would click this pitch and feel seen?",\r
    "What do they do for a living, in one phrase?",\r
    "What did they try this week that didn't work?",\r
    "Why would they rather hear from you than from a brand?",\r
  ],\r
  bodyTemplate: "## Who This Is For\\n[One specific person, named out loud. Not a segment \u2014 a person.]\\n\\n## What They Tried This Week\\n[One thing they did that didn't work. Be concrete.]",\r
  suggestedDurationSeconds: 30,\r
  layoutHint: "easy-first-pitch",\r
  notes: "Easy First Pitch kinds are the 'minimum viable' surface \u2014 short prompts, no fake ceremony. If the user is going to spend 30 seconds on this beat, it should be on naming the person, not on the pain.",\r
}\r
`;

// src/segments/easy-one-sentence-promise.json5
var easy_one_sentence_promise_default = `// Segment definition: Easy First Pitch \u2014 One-Sentence Promise\r
// Second beat of the 3-segment "Easy First Pitch" framework. The\r
// "promise" IS the solution reveal, condensed to one sentence (per\r
// \`docs/templates.md\`). The plan's "show your face" encouragement\r
// is folded into the prompt questions, not the body template.\r
{\r
  id: "easy_one_sentence_promise",\r
  name: "The One-Sentence Promise",\r
  description: "The one sentence that, if the viewer reads nothing else, lands. Maps to the DRTV \`solution_reveal\` condensed to its minimum.",\r
  promptQuestions: [\r
    "What does your product or project actually DO, in one sentence?",\r
    "What does the world look like for the user 24 hours after they use it?",\r
    "What's the smallest believable claim a stranger would still find bold?",\r
    "What is the cheapest, fastest way to test if the promise is true?",\r
  ],\r
  bodyTemplate: "## The Promise\\n[One sentence. Verb first. No adverbs.]\\n\\n## What Changes in 24 Hours\\n[The smallest observable delta. The viewer's first proof.]",\r
  suggestedDurationSeconds: 30,\r
  layoutHint: "easy-first-pitch",\r
  notes: "The 'show your face' video is a separate step in the wizard (PFP-1 \xA73.2 step 3, the bunny/embed/skip prompt). The promise is text-first; the video is a complement, not a replacement.",\r
}\r
`;

// src/segments/easy-proof-or-cta.json5
var easy_proof_or_cta_default = `// Segment definition: Easy First Pitch \u2014 Proof or CTA\r
// Third beat of the 3-segment "Easy First Pitch" framework. The\r
// "proof or CTA" beat is intentionally permissive \u2014 a link, a\r
// button, a single proof point, or a quiet "if you want, here's\r
// where" surface. The DRTV \`cta_close\` is the fully-scripted\r
// version; the Easy First Pitch version is the smallest honest\r
// version of that beat.\r
{\r
  id: "easy_proof_or_cta",\r
  name: "Proof or CTA",\r
  description: "The smallest honest proof or call-to-action. One short paragraph; a link, a button, a single screenshot, or a quiet 'if you want' line.",\r
  promptQuestions: [\r
    "What's the smallest proof that the promise is real \u2014 a number, a quote, a screenshot?",\r
    "Is there a link, button, or HP code the viewer should click?",\r
    "Is there a thing you want the viewer to do in the next 60 seconds?",\r
    "What's the honest 'no urgency' line \u2014 what would you say if the launch is in 2 months, not 2 days?",\r
  ],\r
  bodyTemplate: "## The Proof (or the Quiet Link)\\n[One number, one quote, one screenshot \u2014 or just the link.]\\n\\n## Where To Go\\n- [URL]\\n- [HP code, if you have one]\\n- [Or: 'If you want, just reply to this pitch.']",\r
  suggestedDurationSeconds: 30,\r
  layoutHint: "easy-first-pitch",\r
  notes: "Easy First Pitch is 'crappy but live beats perfect late' (marketing-and-promotion.md \xA72.8). A link with one sentence is fine. A button with no link is not.",\r
}\r
`;

// src/segments.ts
var rawSegmentJsons = [
  hook_teaser_default,
  pain_deep_dive_default,
  solution_reveal_default,
  demo_mechanics_default,
  proof_testimonials_default,
  objection_handler_default,
  offer_stack_default,
  cta_close_default,
  roll_in_recap_default,
  easy_who_its_for_default,
  easy_one_sentence_promise_default,
  easy_proof_or_cta_default
];
var RICH_IDS = /* @__PURE__ */ new Set([
  "hook_teaser",
  "pain_deep_dive",
  "solution_reveal",
  "demo_mechanics",
  "proof_testimonials",
  "objection_handler",
  "offer_stack",
  "cta_close",
  "roll_in_recap",
  "easy_who_its_for",
  "easy_one_sentence_promise",
  "easy_proof_or_cta"
]);
var cached2 = null;
function loadSegments() {
  if (cached2) return cached2;
  const rich = {};
  for (const raw of rawSegmentJsons) {
    const parsed = JSON52.parse(raw);
    if (!parsed.id) {
      throw new Error(
        `Segment JSON5 file is missing required field 'id':
${raw.slice(0, 200)}`
      );
    }
    rich[parsed.id] = parsed;
  }
  const out = {};
  for (const k of SECTION_KINDS) {
    if (RICH_IDS.has(k) && rich[k]) {
      out[k] = rich[k];
    } else {
      out[k] = synthesizeStub(k);
    }
  }
  cached2 = out;
  return cached2;
}
var SECTION_SCAFFOLDS = {
  // ─── Classic copywriting kinds ──────────────────────────────────────
  problem: {
    description: "Name the pain. One specific problem the reader already feels \u2014 not the category, the moment.",
    promptQuestions: [
      "What is the one problem the viewer is sitting with right now?",
      "How would they describe it in their own words to a friend?",
      "What did they try last week that didn't work?",
      "Why is the problem getting worse, not better?"
    ],
    bodyTemplate: "## The Problem\n[State the pain in the reader's own words \u2014 one short paragraph, no jargon.]\n\n## Why Now\n[What changed last week / last month that made this problem urgent?]\n\n## What They've Already Tried\n[The one workaround the reader is using today. Be specific.]"
  },
  agitate: {
    description: "Rub the wound. Make the cost of inaction concrete \u2014 what's at stake if they ignore this?",
    promptQuestions: [
      "What does this problem cost the reader every day it's left unsolved?",
      "What's the worst-case outcome 6 months from now if nothing changes?",
      "Who else is affected by the reader's inaction (their team / family / customers)?",
      "What does the reader keep telling themselves to avoid thinking about this?"
    ],
    bodyTemplate: "## The Cost of Inaction\n[What does this problem cost the reader in dollars / hours / lost opportunities per week?]\n\n## What Gets Worse\n[Walk forward 3 / 6 / 12 months. What compounds?]\n\n## The Ripple\n[Who else pays for this \u2014 the team, the family, the customers?]"
  },
  solution: {
    description: "Reveal the answer. Position your product / service / approach as the cleanest path out of the agitated problem.",
    promptQuestions: [
      "What's the one-sentence answer to the problem above?",
      "Why is this the path of least resistance vs the alternatives?",
      "What does life look like one week after the reader adopts this?",
      "What's the single proof point that earns the reader's belief?"
    ],
    bodyTemplate: "## The Answer\n[Your solution, in the reader's words. One paragraph.]\n\n## Why This Works\n[What's different about this approach vs what the reader has already tried.]\n\n## What Changes\n[The reader's day-to-day, one week after they adopt this.]"
  },
  hook: {
    description: "Earn the next 30 seconds of attention. A sharp pattern-break, surprising stat, or visceral pain.",
    promptQuestions: [
      "What's the boldest one-sentence version of the pain or the promise?",
      "What visual or stat could stop the scroll in 5 seconds?",
      "What does the reader NOT expect to hear in the next 8 seconds?",
      "Why should the reader NOT click away right now?"
    ],
    bodyTemplate: "## Hook\n[The sharpest, most specific version of the pain \u2014 one line, no softening.]\n\n## Why Stay\n[The one reason to keep watching. Make it concrete.]"
  },
  interest: {
    description: "Build curiosity. A surprising fact, a behind-the-scenes peek, or a reframing of the problem.",
    promptQuestions: [
      "What's the one fact most readers don't know that re-frames the problem?",
      "What's the most counterintuitive thing about how the solution actually works?",
      "What's behind the curtain \u2014 the 'how we do this' the reader hasn't seen?",
      "What did you believe yourself, before you realized you were wrong?"
    ],
    bodyTemplate: "## The Reframe\n[The surprising fact or new angle that earns the next 30 seconds.]\n\n## Behind the Curtain\n[The 'how we do this' the reader hasn't seen before.]"
  },
  desire: {
    description: "Make them want it. Show what life looks like, paint the after-state in vivid, specific terms.",
    promptQuestions: [
      "What does the reader's day look like 30 days after they adopt this?",
      "What's the small, concrete moment that proves it's working?",
      "What do they get to stop doing?",
      "What does the reader's peer / partner / customer notice first?"
    ],
    bodyTemplate: "## The After-State\n[What does the reader's day look like 30 days after they adopt this? Paint in vivid, specific terms.]\n\n## The Small Proof\n[The first concrete moment that proves it's working \u2014 the one the reader will notice.]\n\n## The Ripple\n[What does the reader's team / family / customer notice first?]"
  },
  action: {
    description: "Drive the click. Make the next step feel obvious, low-risk, and time-bound.",
    promptQuestions: [
      "What's the single CTA \u2014 the one button / link / next step?",
      "What's the smallest version of the action that's still a real win?",
      "What's the urgency? Why now, why today?",
      "What's the risk-removal \u2014 the guarantee / refund / no-credit-card-required?"
    ],
    bodyTemplate: "## The Next Step\n[The single CTA \u2014 one button, one link, one action. State it plainly.]\n\n## Why Now\n[The urgency. Why today and not next week?]\n\n## The Risk-Removal\n[The guarantee, the refund window, the no-credit-card-required \u2014 whatever makes the action feel safe.]"
  },
  before: {
    description: "Paint the 'before' state \u2014 vivid, specific, without hope. Make the reader see themselves in it.",
    promptQuestions: [
      "What does the reader's day look like today, before they adopt this?",
      "What is the most concrete moment of frustration in their current state?",
      "What does the reader keep saying to themselves about this problem?",
      "What's the smallest visible sign that the problem is real?"
    ],
    bodyTemplate: "## Before\n[The reader's day, today. Vivid, specific, no hope \u2014 a single concrete moment.]\n\n## The Moment\n[The smallest visible sign that the problem is real. The one a friend would notice.]"
  },
  after: {
    description: "Paint the 'after' state \u2014 vivid, specific, emotionally true. Make the reader want to step into it.",
    promptQuestions: [
      "What does the reader's day look like after they adopt this?",
      "What does the reader get to stop doing?",
      "What do they get to start doing?",
      "What does someone close to them notice first?"
    ],
    bodyTemplate: "## After\n[The reader's day, post-adoption. Vivid, specific, emotionally true \u2014 name a single concrete moment.]\n\n## What You Get Back\n[The hours / dollars / opportunities the reader recovers.]\n\n## Who Notices\n[The person closest to the reader who sees the change first.]"
  },
  bridge: {
    description: "The mechanism \u2014 what makes the leap from before to after actually possible.",
    promptQuestions: [
      "What's the one mechanism that makes the leap possible?",
      "Why is this approach different from what the reader has already tried?",
      "What's the smallest proof \u2014 the first concrete signal it works?",
      "What does the reader NOT have to learn / buy / change to get started?"
    ],
    bodyTemplate: "## The Mechanism\n[The one thing that makes the before\u2192after leap possible. Explain it plainly.]\n\n## Why This Is Different\n[What the reader has already tried, and why this approach isn't that.]\n\n## What's Not Required\n[What the reader does NOT have to learn, buy, or change to get started.]"
  },
  feature: {
    description: "List the capabilities. Plain-spoken bullets \u2014 what the thing actually does, no marketing gloss.",
    promptQuestions: [
      "What does the product actually do, in plain words?",
      "What's the most-used feature, by the median user?",
      "What's the surprising capability \u2014 the one most people don't realize is there?",
      "What's the one thing a power user does that a new user doesn't?"
    ],
    bodyTemplate: "## What It Does\n[The capabilities, in plain words. Three to five bullets.]\n\n## What Surprises People\n[The capability that most users don't discover until week two.]\n\n## What Power Users Do Differently\n[The one thing a power user does that a new user doesn't.]"
  },
  advantage: {
    description: "Translate features into the reader's advantages. 'So what?' for each one.",
    promptQuestions: [
      "For the biggest feature: what's the reader's advantage?",
      "What does the reader GET to do, that they couldn't before?",
      "What do they get to STOP doing?",
      "What's the economic advantage \u2014 dollars saved, hours saved, opportunities unlocked?"
    ],
    bodyTemplate: "## What You Get\n[Translate the biggest features into plain-English advantages \u2014 three to five bullets.]\n\n## What You Stop Doing\n[The workaround the reader can finally retire.]\n\n## The Economic Win\n[Dollars / hours / opportunities \u2014 quantify the advantage.]"
  },
  benefit: {
    description: "The 'so what' arrives \u2014 concrete life-outcomes the reader can picture themselves into.",
    promptQuestions: [
      "What does the reader's day look like 30 days after they adopt this?",
      "What's the small, concrete moment that proves it's working?",
      "What does the reader's peer / partner / customer notice first?",
      "What's the long-tail benefit \u2014 6 / 12 months out?"
    ],
    bodyTemplate: "## The Outcome\n[What does the reader's day look like 30 days after they adopt this? Vivid, specific.]\n\n## The First Proof\n[The small, concrete moment that proves it's working.]\n\n## The Long-Tail\n[What compounds 6 / 12 months out \u2014 the durable win.]"
  },
  promise: {
    description: "The headline promise. One bold sentence the reader will remember and repeat.",
    promptQuestions: [
      "What's the single bold promise \u2014 in one sentence?",
      "What does the reader gain? In one phrase.",
      "What does the reader avoid? In one phrase.",
      "What makes the promise credible \u2014 the proof behind it?"
    ],
    bodyTemplate: "## The Promise\n[The bold one-sentence promise \u2014 what the reader gains, what they avoid.]\n\n## Why It's Credible\n[The proof behind the promise \u2014 one fact, one quote, one moment.]"
  },
  picture: {
    description: "Vivid scene \u2014 what life looks like in vivid, sensory detail after the promise is true.",
    promptQuestions: [
      "What's the specific scene \u2014 the time, place, doing-what \u2014 that proves the promise?",
      "What does the reader see / hear / feel in that moment?",
      "Who else is in the scene \u2014 the partner / colleague / customer?",
      "What's the small, concrete detail that makes it real?"
    ],
    bodyTemplate: "## The Scene\n[The vivid picture \u2014 time, place, the small concrete detail.]\n\n## The Sensory Anchor\n[One detail (sight / sound / feel) that locks the scene in the reader's mind.]"
  },
  proof: {
    description: "Earn belief. One concrete proof point \u2014 a number, a quote, a result.",
    promptQuestions: [
      "What is the one proof point that earns the reader's belief?",
      "What's the specific number / result / time-saved?",
      "Who is the proof from \u2014 the customer / user / expert / founder?",
      "What did they say, in their own words, that crystallizes the result?"
    ],
    bodyTemplate: "## The Proof\n[The one fact / number / quote that earns the reader's belief. Be specific.]\n\n## The Source\n[Who's the proof from \u2014 name them, make it concrete.]"
  },
  push: {
    description: "Drive the next action. Make the reader feel the urgency and the smallness of the step.",
    promptQuestions: [
      "What is the single next step?",
      "Why is the urgency real \u2014 not manufactured?",
      "What's the smallest version of the action that's still a real win?",
      "What's the risk-removal?"
    ],
    bodyTemplate: "## The Next Step\n[The single button / link / action \u2014 stated plainly.]\n\n## Why Now\n[The real urgency \u2014 not manufactured.]\n\n## Make It Safe\n[The guarantee / refund / no-credit-card-required \u2014 whatever removes the risk.]"
  },
  freeform: {
    description: "Anything that doesn't fit the standard kinds. One open canvas \u2014 body is whatever the creator types.",
    promptQuestions: [
      "What is this section for \u2014 what does the reader get out of it?",
      "What's the most important takeaway the reader walks away with?",
      "Is there an example or story that anchors the point?",
      "What's the next step, if any, after this section?"
    ],
    bodyTemplate: "[Free-form. Type the section body here \u2014 there's no prescribed structure. The reader will see this verbatim on the public page.]"
  },
  // ─── Rich infomercial kinds (kept here as a safety net; the canonical
  //     definitions live in the JSON5 files under src/segments/ and the
  //     loader populates `RICH_IDS`-kinds from those. This branch is
  //     unreachable in practice because every infomercial kind has a
  //     JSON5 file. Listed for type completeness. ──────────────────────
  hook_teaser: stubScaffold(),
  pain_deep_dive: stubScaffold(),
  solution_reveal: stubScaffold(),
  demo_mechanics: stubScaffold(),
  proof_testimonials: stubScaffold(),
  objection_handler: stubScaffold(),
  offer_stack: stubScaffold(),
  cta_close: stubScaffold(),
  roll_in_recap: stubScaffold(),
  // ─── Easy First Pitch (PFP-1 §3.3) — rich kinds; same safety net. ──
  easy_who_its_for: stubScaffold(),
  easy_one_sentence_promise: stubScaffold(),
  easy_proof_or_cta: stubScaffold()
};
function stubScaffold() {
  return {
    description: "Authoring scaffold pending. Rich definition expected in src/segments/.",
    promptQuestions: [
      "What's the one thing the reader should walk away knowing from this section?"
    ],
    bodyTemplate: "## Section\n[Body scaffold pending \u2014 author the rich definition in src/segments/]"
  };
}
function synthesizeStub(kind) {
  const scaffold = SECTION_SCAFFOLDS[kind];
  return {
    id: kind,
    name: sectionKindTitle(kind),
    description: scaffold.description,
    promptQuestions: [...scaffold.promptQuestions],
    bodyTemplate: scaffold.bodyTemplate
  };
}
function getSegmentDefinition(kind) {
  return loadSegments()[kind];
}
var SEGMENT_DEFINITIONS = new Proxy({}, {
  get(_target, prop) {
    if (typeof prop === "string") {
      return loadSegments()[prop];
    }
    return void 0;
  },
  ownKeys() {
    return Object.keys(loadSegments());
  },
  getOwnPropertyDescriptor() {
    return { enumerable: true, configurable: true };
  }
});

// src/framework-guidance.ts
import JSON53 from "json5";

// src/frameworks/framework-guidance.json5
var framework_guidance_default = `// Framework: editor guidance for the studio's framework picker.
//
// What this file is:
//   "When is this framework the right choice?" guidance that
//   accompanies every FrameworkId in the picker. Each entry surfaces
//   the framework's sweet-spot (product types, customer psychologies,
//   offer shapes, message goals) and a short "avoid when" note so
//   the picker can render a useful detail-panel alongside the
//   existing name + section outline.
//
// Where the data lives:
//   This file is loaded by \`loadFrameworkGuidance()\` and exported via
//   \`frameworkGuidance(id)\` from \`@honest-pitches/pitch-core/browser\`.
//   The studio's \`StepFrameworkPicker.vue\` consumes it through
//   \`WizardFrameworkOption.guidance\`, which the orchestrator populates
//   by calling the loader.
//
// Why a separate file rather than extending the existing JSON5:
//   The existing JSON5 files (\`easy-first-pitch.json5\` etc.) describe
//   the framework's STRUCTURE (sections, origin, duration). This
//   file describes the framework's EDITORIAL FIT \u2014 different audience,
//   different lifecycle. Conflating the two would entangle structure
//   changes with copy changes; keeping them separate lets us update
//   guidance copy without touching section sequences.
//
// Fields per framework:
//   - summary: one sentence the picker renders under the title
//   - bestForProducts:   array of product types where the framework shines
//   - bestForCustomers:  array of customer psychologies (the "who reads it")
//   - bestForOffers:     array of offer shapes (single SKU, bundle, free trial, etc.)
//   - bestForMessages:   array of message goals (awareness, comparison, urgency, etc.)
//   - avoidWhen:         short list of contexts where this framework is the wrong fit
//
// Each array is intentionally short (2-5 items) \u2014 the picker renders
// these as bullet lists; longer lists don't render well on one screen.
{
  // 2026-06-28: keyed by FrameworkId (the canonical union from
  // \`framework.ts\`). The \`frameworks-guidance.test.ts\` contract test
  // pins that every FrameworkId has a guidance entry \u2014 adding a new
  // FrameworkId to \`FRAMEWORK_IDS\` without adding guidance here fails
  // the build.
  easy_first_pitch: {
    summary: "The shortest credible version of a pitch. Use when you'd rather ship a crappy-first draft than a perfect-on-paper deck.",
    bestForProducts: ["Solo-built software", "Self-published books or courses", "Indie consumer goods", "Service businesses with a single flagship offer"],
    bestForCustomers: ["Early-curious buyers in your own network", "Newsletter / blog readers who already know your name", "Cousin-of-a-cousin referrals"],
    bestForOffers: ["Single product or service", "Pre-order / waitlist", "Free tier with a paid upgrade path", "1:1 consultation"],
    bestForMessages: ["'I made this; here's who it's for.'", "'Here's the one thing I can promise.'", "'Here's the smallest proof I have.'"],
    avoidWhen: ["You sell to enterprise procurement", "Your offer hinges on a 12-step technical comparison", "You're competing on regulatory depth"],
  },
  pas: {
    summary: "Lead with the painful problem, twist the knife, then reveal the answer. Best when the reader already feels the pain and just hasn't met your solution yet.",
    bestForProducts: ["SaaS that fixes a known workflow", "Supplements and health tools", "Coaching and services for a specific pain"],
    bestForCustomers: ["Active sufferers searching for relief", "'I've tried X, Y, Z and I'm still stuck' buyers", "Comparison-mode shoppers looking for the 'this one finally worked' angle"],
    bestForOffers: ["Single SKU or plan", "Free trial with the painful-step called out in the trial", "One-time purchase with a clear before/after"],
    bestForMessages: ["'You already know the problem. Here's the specific kind.'", "'Here's what most people try first \u2014 and why it doesn't work.'", "'Now here's what does.'"],
    avoidWhen: ["The reader doesn't yet recognize the pain (you'd be agitating a phantom)", "Your product is a category-defining novelty with no incumbent to agitate against"],
  },
  aida: {
    summary: "Attention \u2192 Interest \u2192 Desire \u2192 Action. The classic funnel; best when you're introducing something new and need to earn interest before you can earn a sale.",
    bestForProducts: ["New consumer categories (no prior comparison set)", "Course launches with a known teacher", "DTC brands with a strong visual hook"],
    bestForCustomers: ["Cold audiences scrolling social feeds", "Top-of-funnel search traffic on broad keywords", "Lookalike audiences of warm buyers"],
    bestForOffers: ["Lead magnet \u2192 tripwire \u2192 core offer ladder", "Single hero product with a clear transformation"],
    bestForMessages: ["'Stop scrolling \u2014 this is for you.'", "'Here's why you should care.'", "'Here's what it feels like to have it.'", "'Here's how to start.'"],
    avoidWhen: ["The reader is already mid-funnel (use PAS or BAB instead)", "You have no strong hook \u2014 AIDA's first step is unforgiving"],
  },
  bab: {
    summary: "Before \u2192 After \u2192 Bridge. Sell the transformation, then explain how you get there. Best when the outcome is vivid and the reader already trusts that 'how' is solvable.",
    bestForProducts: ["Fitness / wellness programs", "Coaching and mentorship", "Home renovation / lifestyle upgrades"],
    bestForCustomers: ["Aspirational buyers who can picture the After", "Past buyers of 'good enough' alternatives", "Friends-of-friends referrals with a clear before/after to point at"],
    bestForOffers: ["Cohort-based program", "Multi-week transformation", "Premium 1:1 service"],
    bestForMessages: ["'Imagine if X were true for you.'", "'Here's exactly what that looks like in your life.'", "'The bridge is: [your method].'"],
    avoidWhen: ["The reader is skeptical that the transformation is achievable (lead with proof first)", "Your offer is a one-shot transaction with no journey to bridge"],
  },
  fab: {
    summary: "Feature \u2192 Advantage \u2192 Benefit. Walk from the thing to the so-what. Best for technical buyers who want to know what they're actually getting before they care about feelings.",
    bestForProducts: ["B2B SaaS with deep feature parity vs. alternatives", "Developer tools", "Hardware with a spec sheet"],
    bestForCustomers: ["Engineers and analysts doing comparisons", "Procurement teams writing evaluation matrices", "Existing category buyers upgrading"],
    bestForOffers: ["Tiered plans with feature deltas", "Per-seat pricing with capability unlocks", "Hardware SKUs with spec variants"],
    bestForMessages: ["'It has X.'", "'Which means you can Y.'", "'So you get Z.'"],
    avoidWhen: ["Cold audiences who don't yet care about features (use AIDA first)", "Luxury / lifestyle purchases where the spec sheet is a feature, not a benefit"],
  },
  pss: {
    summary: "Problem \u2192 Solution \u2192 Success. PAS without the knife-twist. Best when the pain is mild, the buyer is rational, and the decision should feel obvious.",
    bestForProducts: ["Productivity / workflow tools", "Self-serve SaaS with a free tier", "Books / courses for a known audience"],
    bestForCustomers: ["Time-poor decision-makers", "Repeat buyers who already trust the category", "Internal tools with one clear champion"],
    bestForOffers: ["Self-serve free \u2192 paid upgrade", "Annual plan with a per-month savings call-out", "Single-book or single-course purchase"],
    bestForMessages: ["'You have X.'", "'Use Y.'", "'Then Z.'"],
    avoidWhen: ["The pain needs agitation to be felt (use PAS)", "You need to seed a brand new category (use AIDA)"],
  },
  "4ps": {
    summary: "Picture \u2192 Promise \u2192 Prove \u2192 Push. Promise the picture, prove it's real, push the reader to act. Best for classic long-form sales pages with a clear single offer.",
    bestForProducts: ["Premium DTC", "Coaching and masterminds", "High-ticket courses"],
    bestForCustomers: ["Warm-list buyers who have been on the fence", "Webinar attendees", "Long-form readers (blog subscribers, podcast listeners)"],
    bestForOffers: ["Single high-ticket offer with payment plans", "Cohort launch with a deadline", "Application-only services"],
    bestForMessages: ["'Imagine [vivid picture].' (long-form, sensory)", "'And I promise [concrete outcome].' (specific, dated)", "'Here's proof from [N] people.' (named, dated, specific)", "'Doors close [date]. Here's how to start.'"],
    avoidWhen: ["You don't have proof yet (the 'Prove' step collapses)", "The product is a low-consideration impulse buy"],
  },
  custom: {
    summary: "You already know what you want. Use this when none of the named frameworks fit and you're willing to design the sections yourself.",
    bestForProducts: ["Anything \u2014 but only if you know exactly what you want"],
    bestForCustomers: ["Whoever your audience is"],
    bestForOffers: ["Whatever you're selling"],
    bestForMessages: ["Whatever message you've tested"],
    avoidWhen: ["You're new to pitching (start with Easy First Pitch or PAS)", "You'd benefit from a proven structure to anchor your copy (use PAS or AIDA)"],
  },
  drtv_classic_infomercial: {
    summary: "The classic 28:30 DRTV spot structure: hook \u2192 problem \u2192 agitate \u2192 demo \u2192 proof \u2192 offer \u2192 close. Best when you have a physical product, a strong demo, and a media buy to amortize.",
    bestForProducts: ["Physical consumer goods with a visible before/after", "Health & beauty with a real demo", "Household products with a 'gross problem' reveal"],
    bestForCustomers: ["TV-scale cold audiences", "Late-night / daytime viewers", "Impulse-response buyers responding to 'as seen on TV'"],
    bestForOffers: ["Single SKU with a 'but wait, there's more' ladder", "Multi-pack bonuses (buy 2 get 1)", "Strong guarantee (90-day, money-back)"],
    bestForMessages: ["'If you have [problem], watch this.'", "Product demo with call-outs", "Celebrity / expert endorsement", "'But wait \u2014 call in the next 20 minutes and\u2026'"],
    avoidWhen: ["You're bootstrapping with no media budget (use PAS)", "Your product is digital / SaaS (use BAB or FAB)", "You're selling to a B2B audience"],
  },
  drtv_long_form_spot: {
    summary: "The 58:30 long-form program: full story arc with multiple testimonials, extended demo, and a layered offer stack. Best when you have a 30+ minute media placement and a high-LTV offer.",
    bestForProducts: ["Premium supplements with a 'real science' angle", "Fitness programs with transformation stories", "High-ticket physical products (mattresses, fitness equipment)"],
    bestForCustomers: ["Late-night TV viewers", "Engaged 'as seen on TV' repeat buyers", "Customers who respond to long-form storytelling"],
    bestForOffers: ["High-ticket with installment payments", "Continuity / autoship with a deep first-bottle discount", "Stacked bonuses (the 'value ladder' close)"],
    bestForMessages: ["'My name is [host], and for the next hour\u2026'", "Multiple transformation stories", "Extended demo with side-by-side comparison", "Layered offer reveal (3 stacked bonuses)"],
    avoidWhen: ["You don't have a 30-minute media buy (use DRTV classic instead)", "Your product is digital / SaaS (use BAB)", "You're not ready for a continuity program (the long-form close depends on it)"],
  },
}`;

// src/framework-guidance.ts
var cached3 = null;
function loadFrameworkGuidance() {
  if (cached3) return cached3;
  const raw = JSON53.parse(framework_guidance_default);
  const out = {};
  for (const id of FRAMEWORK_IDS) {
    const entry = raw[id];
    if (!entry) {
      throw new Error(
        `framework-guidance.json5 is missing an entry for FrameworkId '${id}'. Add a guidance block (summary + bestFor* arrays + avoidWhen) for every FrameworkId.`
      );
    }
    out[id] = entry;
  }
  cached3 = out;
  return cached3;
}
function frameworkGuidance(id) {
  return loadFrameworkGuidance()[id];
}

// src/suggested-sections.ts
function suggestedSectionsForFramework(framework) {
  if (FRAMEWORK_SECTIONS[framework].length === 0) {
    const defs = loadFrameworks();
    const def = defs[framework];
    if (def && def.segmentSequence.length > 0) {
      FRAMEWORK_SECTIONS[framework] = def.segmentSequence;
    }
  }
  const kinds = FRAMEWORK_SECTIONS[framework];
  if (kinds.length === 0) {
    throw new Error(
      `Framework '${framework}' has no segment sequence registered. Did the JSON5 loader run?`
    );
  }
  const segments = loadSegments();
  return kinds.map((kind) => {
    const segment = segments[kind];
    return {
      kind,
      body: segment?.bodyTemplate ?? "",
      mediaIds: []
    };
  });
}

// src/load-pitch-sections.ts
function loadPitchSections({
  pitchId,
  doc,
  rows,
  log = () => {
  }
}) {
  const legacy = parsePitchSectionsColumn(
    doc.sections ?? null
  );
  const fromRows = pitchSectionsRowsToArray(rows, { pitchId });
  if (fromRows.length > 0 && legacy.length > 0 && fromRows.length < legacy.length) {
    log(
      `[load-pitch-sections] self-heal | pitchId=${pitchId} | new=${fromRows.length} rows legacy=${legacy.length} sections | falling back to legacy column`
    );
    return {
      sections: legacy,
      source: "legacy",
      disagreement: { newCount: fromRows.length, legacyCount: legacy.length }
    };
  }
  if (fromRows.length > 0) {
    return { sections: fromRows, source: "new" };
  }
  if (legacy.length > 0) {
    return { sections: legacy, source: "legacy" };
  }
  return { sections: [], source: "empty" };
}

// src/merge-sections.ts
function ensureFrameworkSectionsLoaded(framework) {
  if (FRAMEWORK_SECTIONS[framework].length === 0) {
    loadFrameworks();
  }
  return FRAMEWORK_SECTIONS[framework];
}
function deepCopySection(section) {
  const out = {
    kind: section.kind,
    body: section.body,
    mediaIds: [...section.mediaIds]
  };
  if (section.headline) out.headline = section.headline;
  if (section.sub) out.sub = section.sub;
  if (section.cues) {
    out.cues = section.cues.map((c) => ({
      clipId: c.clipId,
      startSeconds: c.startSeconds,
      ...c.endSeconds != null ? { endSeconds: c.endSeconds } : {}
    }));
  }
  return out;
}
function mismatchedSectionKinds(existing, framework) {
  if (!isFrameworkId(framework)) return [];
  if (framework === "custom") return [];
  const required = new Set(ensureFrameworkSectionsLoaded(framework));
  const extras = existing.map((s) => s.kind).filter((k) => !required.has(k));
  return [...new Set(extras)].sort();
}
function mergeSectionsForFramework(existing, framework) {
  if (!isFrameworkId(framework)) {
    throw new Error(`Unknown framework: ${framework}`);
  }
  if (framework === "custom") {
    return existing.map(deepCopySection);
  }
  const requiredKinds = ensureFrameworkSectionsLoaded(framework);
  const suggested = suggestedSectionsForFramework(framework);
  const byKind = /* @__PURE__ */ new Map();
  for (const section of existing) {
    if (!byKind.has(section.kind)) {
      byKind.set(section.kind, section);
    }
  }
  const requiredSet = new Set(requiredKinds);
  const merged = [];
  for (const seed of suggested) {
    const prior = byKind.get(seed.kind);
    merged.push(prior ? deepCopySection(prior) : deepCopySection(seed));
  }
  const extras = existing.filter((s) => !requiredSet.has(s.kind)).map(deepCopySection).sort((a, b) => a.kind.localeCompare(b.kind));
  merged.push(...extras);
  return merged;
}
export {
  DEFAULT_MIN_SAMPLE_PER_ARM,
  FRAMEWORK_DEFINITIONS,
  FRAMEWORK_IDS,
  FRAMEWORK_LABELS,
  FRAMEWORK_SECTIONS,
  PITCH_CLIP_SOURCES,
  PITCH_LAYOUT_IDS,
  PITCH_LIFECYCLE_STATES,
  SECTION_KINDS,
  SEGMENT_DEFINITIONS,
  TEST_ARMS,
  TEST_CONCLUSIONS,
  TEST_PRIMARY_METRICS,
  TEST_STATUSES,
  assertFrameworkSwitchable,
  assertSameTestBase,
  baseKey,
  buildPitchVariantIndexes,
  canChangeFramework,
  canConcludeTest,
  canHardDeleteFromLifecycle,
  canHardDeleteFromRow,
  canMutateTestAssignment,
  canTransitionTest,
  deserializeRowToSection,
  detectPitchClipSourceFromUrl,
  frameworkDefinition,
  frameworkGuidance,
  frameworkOutlineLabel,
  getSegmentDefinition,
  groupByOffer,
  isAudienceVariantPair,
  isEditablePitch,
  isFrameworkId,
  isHardDeletableFromRow,
  isPitchClipSource,
  isPitchLayoutId,
  isPitchLifecycleState,
  isSectionKind,
  isTestArm,
  isTestConclusion,
  isTestPrimaryMetric,
  isTestStatus,
  lifecycleFromRow,
  loadFrameworkGuidance,
  loadFrameworks,
  loadPitchSections,
  loadSegments,
  mergeSectionsForFramework,
  mismatchedSectionKinds,
  normalizeClips,
  normalizeCreatePitchInput,
  normalizePitchContent,
  normalizePitchLayout,
  parseClipsJson,
  parsePitchSectionsColumn,
  parseSectionsJson,
  pitchSectionsRowsToArray,
  renderSectionBody,
  sanitizeSectionHtml,
  sectionKindTitle,
  sectionsFromDescription,
  serializeSectionForRow,
  siblingPicker,
  siblingsByBase,
  suggestedSectionsForFramework,
  synthesizeClipFromLegacyRow,
  synthesizeClipFromSection,
  testIdForPitch,
  validateCues,
  validateSectionsForFramework,
  validateTestAssignmentCoupling
};
//# sourceMappingURL=index.js.map